import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { QrCode, Trash2 } from 'lucide-react';
import {
  Button,
  EmptyState,
  ErrorState,
  Skeleton,
  StatusBadge,
  AdminPage,
} from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { useMe } from '@/auth/useMe';
import { useSettings } from '@/access/SettingsContext';
import { formatDateTime, timeLeftLabel } from '@/lib/dates';
import { formatPhone } from '@/lib/phone';
import { errorMessage } from '@/lib/errors';
import { useNow } from '@/lib/useNow';
import { label, applicationStatus } from '@/lib/i18n/he';
import { useEmployeeOptions } from '../api';
import {
  joinUrl,
  useApplicationMutations,
  useApplications,
  useInviteMutations,
  useInvites,
  type Application,
} from './api';
import { QrPoster } from './QrPoster';

/** הרשמה דרך QR: יצירת הזמנות ואישור בקשות ממתינות (PRD 5.17). */
export function ApplicationsPage() {
  const canManage = usePerm('employees.manage');
  const { me } = useMe();
  const { appUrl } = useSettings();
  const { data: options } = useEmployeeOptions();
  const now = useNow();

  const invites = useInvites();
  const applications = useApplications();
  const inviteMutations = useInviteMutations(me?.employeeId);
  const { approve, reject } = useApplicationMutations();

  const [shownToken, setShownToken] = useState<string | null>(null);
  const [branchId, setBranchId] = useState('');
  const [extraBranchIds, setExtraBranchIds] = useState<string[]>([]);
  const [jobTitleId, setJobTitleId] = useState('');
  const [roleId, setRoleId] = useState('');
  const [hours, setHours] = useState(24);
  const [maxUses, setMaxUses] = useState(1);
  const [error, setError] = useState<string | null>(null);

  if (!canManage) return <Navigate to="/admin" replace />;

  const pending = (applications.data ?? []).filter((a) => a.status === 'pending');
  const handled = (applications.data ?? []).filter((a) => a.status !== 'pending');

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  return (
    <AdminPage
      backTo="/admin/employees"
      title="הרשמה ב QR"
      subtitle="יצירת קוד לסניף, ואישור מי שנרשם דרכו"
    >
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {shownToken && (
        <QrPoster url={joinUrl(appUrl, shownToken)} onClose={() => setShownToken(null)} />
      )}

      <section className="grid gap-3">
        <h2 className="m-0 font-display text-display-m font-semibold">יצירת QR חדש</h2>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="סניף" id="invite-branch">
            <select
              id="invite-branch"
              value={branchId}
              onChange={(event) => setBranchId(event.target.value)}
              className={INPUT}
            >
              <option value="">ללא סניף</option>
              {(options?.branches ?? []).map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="הרשאה מוצעת" id="invite-role">
            <select
              id="invite-role"
              value={roleId}
              onChange={(event) => setRoleId(event.target.value)}
              className={INPUT}
            >
              <option value="">ללא הרשאה</option>
              {(options?.roles ?? []).map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </Field>

          <div className="grid content-start gap-1 sm:col-span-2">
            <span className="text-label font-medium">סניפים נוספים</span>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {(options?.branches ?? [])
                .filter((branch) => branch.id !== branchId)
                .map((branch) => (
                  <label
                    key={branch.id}
                    className="flex min-h-11 cursor-pointer items-center gap-2 text-body"
                  >
                    <input
                      type="checkbox"
                      checked={extraBranchIds.includes(branch.id)}
                      onChange={(event) =>
                        setExtraBranchIds((prev) =>
                          event.target.checked
                            ? [...prev, branch.id]
                            : prev.filter((id) => id !== branch.id),
                        )
                      }
                      className="size-5"
                    />
                    {branch.name}
                  </label>
                ))}
            </div>
            <p className="m-0 text-body-sm text-muted">
              מי שיירשם דרך הקוד הזה ישויך גם לסניפים האלה, ויראה את הסידור וההודעות שלהם.
            </p>
          </div>

          <Field label="תפקיד מוצע" id="invite-job">
            <select
              id="invite-job"
              value={jobTitleId}
              onChange={(event) => setJobTitleId(event.target.value)}
              className={INPUT}
            >
              <option value="">ללא תפקיד</option>
              {(options?.jobTitles ?? []).map((job) => (
                <option key={job.id} value={job.id}>
                  {job.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="תוקף בשעות" id="invite-hours">
            <input
              id="invite-hours"
              type="number"
              min={1}
              max={720}
              dir="ltr"
              value={hours}
              onChange={(event) => setHours(Number(event.target.value))}
              className={INPUT}
            />
          </Field>

          <Field
            label="כמה אנשים יכולים להירשם"
            id="invite-uses"
            hint="ליום גיוס אפשר להעלות, למשל 10"
          >
            <input
              id="invite-uses"
              type="number"
              min={1}
              max={100}
              dir="ltr"
              value={maxUses}
              onChange={(event) => setMaxUses(Number(event.target.value))}
              className={INPUT}
            />
          </Field>
        </div>

        <div>
          <Button
            variant="primary"
            disabled={inviteMutations.create.isPending || hours < 1 || maxUses < 1}
            onClick={() =>
              run(async () => {
                const token = await inviteMutations.create.mutateAsync({
                  branch_id: branchId || null,
                  extra_branch_ids: extraBranchIds.filter((id) => id !== branchId),
                  job_title_id: jobTitleId || null,
                  role_id: roleId || null,
                  hours,
                  max_uses: maxUses,
                });
                setShownToken(token);
              })
            }
          >
            <QrCode size={20} strokeWidth={1.75} aria-hidden="true" />
            יצירה והצגת QR
          </Button>
        </div>
      </section>

      <section className="grid gap-3">
        <h2 className="m-0 font-display text-display-m font-semibold">
          בקשות ממתינות {pending.length > 0 && `(${pending.length})`}
        </h2>

        {applications.isPending && <Skeleton className="h-20 w-full" />}
        {applications.isError && <ErrorState onRetry={() => void applications.refetch()} />}
        {applications.data && pending.length === 0 && (
          <EmptyState title="אין בקשות ממתינות" description="כשמישהו ירשם ב QR, הוא יופיע כאן." />
        )}

        <ul className="grid list-none gap-2 p-0">
          {pending.map((application) => (
            <PendingRow
              key={application.id}
              application={application}
              options={options}
              onApprove={(values) =>
                run(() => approve.mutateAsync({ id: application.id, ...values }))
              }
              onReject={() => run(() => reject.mutateAsync(application.id))}
            />
          ))}
        </ul>
      </section>

      {handled.length > 0 && (
        <section className="grid gap-3">
          <h2 className="m-0 font-display text-display-m font-semibold">בקשות שטופלו</h2>
          <ul className="grid list-none gap-2 p-0">
            {handled.map((application) => (
              <li
                key={application.id}
                className="flex items-center justify-between gap-3 rounded-md border border-line p-3"
              >
                <span>
                  {application.first_name} {application.last_name}
                </span>
                <StatusBadge tone={application.status === 'approved' ? 'good' : 'bad'}>
                  {label(applicationStatus, application.status)}
                </StatusBadge>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="grid gap-3">
        <h2 className="m-0 font-display text-display-m font-semibold">קודי QR פעילים</h2>

        {invites.isPending && <Skeleton className="h-16 w-full" />}
        {invites.data && invites.data.length === 0 && (
          <p className="m-0 text-body-sm text-muted">עוד לא נוצרו קודים.</p>
        )}

        <ul className="grid list-none gap-2 p-0">
          {(invites.data ?? []).map((invite) => {
            const expired = new Date(invite.expires_at).getTime() < now;
            const exhausted = invite.used_count >= invite.max_uses;
            return (
              <li
                key={invite.id}
                className="flex flex-wrap items-center gap-3 rounded-md border border-line p-3"
              >
                <div className="min-w-0 flex-1 grid gap-1">
                  <span className="flex flex-wrap items-center gap-2 text-title">
                    {invite.branches?.name ?? 'ללא סניף'}
                    {/* הסניפים הנוספים והתפקיד הם מה שהקוד יקבע למי
                        שיירשם דרכו, ולכן הם חלק מזהות הקוד */}
                    {invite.registration_invite_branches.length > 0 && (
                      <span className="text-body-sm text-muted">
                        גם{' '}
                        {invite.registration_invite_branches
                          .map((row) => row.branches?.name)
                          .filter(Boolean)
                          .join(', ')}
                      </span>
                    )}
                    {invite.job_titles?.name && (
                      <span className="text-body-sm text-muted">· {invite.job_titles.name}</span>
                    )}
                    {invite.roles?.name && (
                      <span className="text-body-sm text-muted">· {invite.roles.name}</span>
                    )}
                    {expired && <StatusBadge tone="bad">פג תוקף</StatusBadge>}
                    {!expired && exhausted && <StatusBadge tone="warn">מוצה</StatusBadge>}
                    {!expired && !exhausted && (
                      <StatusBadge tone="good">{timeLeftLabel(invite.expires_at)}</StatusBadge>
                    )}
                  </span>
                  <span className="text-body-sm text-muted">
                    נוצל {invite.used_count} מתוך {invite.max_uses} · נוצר{' '}
                    {formatDateTime(invite.created_at)}
                  </span>
                </div>

                <div className="flex items-center gap-1">
                  <IconButton label="הצגת QR" onClick={() => setShownToken(invite.token)}>
                    <QrCode size={20} strokeWidth={1.75} aria-hidden="true" />
                  </IconButton>
                  <IconButton
                    label="מחיקה"
                    onClick={() => run(() => inviteMutations.remove.mutateAsync(invite.id))}
                  >
                    <Trash2 size={20} strokeWidth={1.75} aria-hidden="true" />
                  </IconButton>
                </div>
              </li>
            );
          })}
        </ul>
      </section>
    </AdminPage>
  );
}

const INPUT = 'min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink';

function Field({
  label: text,
  id,
  hint,
  children,
}: {
  label: string;
  id: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-1">
      <label htmlFor={id} className="text-label font-medium">
        {text}
      </label>
      {children}
      {hint && <p className="m-0 text-body-sm text-muted">{hint}</p>}
    </div>
  );
}

function PendingRow({
  application,
  options,
  onApprove,
  onReject,
}: {
  application: Application;
  options:
    | {
        branches: { id: string; name: string }[];
        jobTitles: { id: string; name: string }[];
        roles: { id: string; name: string }[];
      }
    | undefined;
  onApprove: (values: { branch_id: string; job_title_id: string | null; role_id: string }) => void;
  onReject: () => void;
}) {
  // נפתח על מה שהוצע בקוד. המנהל כבר קבע את זה כשיצר אותו, ואין
  // סיבה שימלא שוב; הוא עדיין יכול לשנות לפני האישור.
  const suggested = application.registration_invites;
  const [branchId, setBranchId] = useState(suggested?.branch_id ?? '');
  const [jobTitleId, setJobTitleId] = useState(suggested?.job_title_id ?? '');
  const [roleId, setRoleId] = useState(suggested?.role_id ?? '');

  const verified = application.phone_verified_at !== null;

  return (
    <li className="grid gap-3 rounded-md border border-line bg-raised p-3">
      <div className="grid gap-1">
        <span className="flex flex-wrap items-center gap-2 text-title font-medium">
          {application.first_name} {application.last_name}
          {verified ? (
            <StatusBadge tone="good">טלפון אומת</StatusBadge>
          ) : (
            <StatusBadge tone="warn">טלפון לא אומת</StatusBadge>
          )}
        </span>
        <span className="text-body-sm text-muted" dir="ltr">
          {formatPhone(application.phone)}
        </span>
        {application.email && (
          <span className="text-body-sm text-muted" dir="ltr">
            {application.email}
          </span>
        )}
        {application.note && <span className="text-body-sm">{application.note}</span>}
        <span className="text-body-sm text-muted">
          נשלחה {formatDateTime(application.created_at)}
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="סניף *" id={`approve-branch-${application.id}`}>
          <select
            id={`approve-branch-${application.id}`}
            value={branchId}
            onChange={(event) => setBranchId(event.target.value)}
            className={INPUT}
          >
            <option value="">בחר/י</option>
            {(options?.branches ?? []).map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="תפקיד" id={`approve-job-${application.id}`}>
          <select
            id={`approve-job-${application.id}`}
            value={jobTitleId}
            onChange={(event) => setJobTitleId(event.target.value)}
            className={INPUT}
          >
            <option value="">ללא תפקיד</option>
            {(options?.jobTitles ?? []).map((job) => (
              <option key={job.id} value={job.id}>
                {job.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="תפקיד הרשאות *" id={`approve-role-${application.id}`}>
          <select
            id={`approve-role-${application.id}`}
            value={roleId}
            onChange={(event) => setRoleId(event.target.value)}
            className={INPUT}
          >
            <option value="">בחר/י</option>
            {(options?.roles ?? []).map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {!verified && (
        <p className="m-0 text-body-sm text-warn">אי אפשר לאשר לפני שהטלפון אומת בקוד SMS.</p>
      )}

      <div className="flex gap-2">
        <Button
          variant="primary"
          disabled={!verified || branchId === '' || roleId === ''}
          onClick={() =>
            onApprove({
              branch_id: branchId,
              job_title_id: jobTitleId || null,
              role_id: roleId,
            })
          }
        >
          אישור ויצירת עובד/ת
        </Button>
        <Button variant="secondary" onClick={onReject}>
          דחייה
        </Button>
      </div>
    </li>
  );
}

function IconButton({
  label: text,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={text}
      title={text}
      onClick={onClick}
      className="inline-flex size-11 cursor-pointer items-center justify-center rounded-sm bg-transparent text-ink"
    >
      {children}
    </button>
  );
}
